/**
 * Utilitário para traduzir descrições do Stripe para o idioma selecionado
 * 
 * @description O Stripe retorna descrições em inglês que precisam ser traduzidas
 * para exibição no frontend de acordo com o idioma do usuário
 * 
 * @version 1.0
 * @created 2025-12-19
 */

// Mapeamento de meses em inglês para tradução
const monthTranslations: Record<string, Record<string, string>> = {
  'pt-BR': {
    'Jan': 'Jan', 'Feb': 'Fev', 'Mar': 'Mar', 'Apr': 'Abr',
    'May': 'Mai', 'Jun': 'Jun', 'Jul': 'Jul', 'Aug': 'Ago',
    'Sep': 'Set', 'Oct': 'Out', 'Nov': 'Nov', 'Dec': 'Dez'
  },
  'es-ES': {
    'Jan': 'Ene', 'Feb': 'Feb', 'Mar': 'Mar', 'Apr': 'Abr',
    'May': 'May', 'Jun': 'Jun', 'Jul': 'Jul', 'Aug': 'Ago',
    'Sep': 'Sep', 'Oct': 'Oct', 'Nov': 'Nov', 'Dec': 'Dic'
  },
  'en-US': {
    'Jan': 'Jan', 'Feb': 'Feb', 'Mar': 'Mar', 'Apr': 'Apr',
    'May': 'May', 'Jun': 'Jun', 'Jul': 'Jul', 'Aug': 'Aug',
    'Sep': 'Sep', 'Oct': 'Oct', 'Nov': 'Nov', 'Dec': 'Dec'
  }
};

// Mapeamento de termos do Stripe para cada idioma
const termTranslations: Record<string, Record<string, string>> = {
  'pt-BR': {
    'at': 'por',
    'month': 'mês',
    'year': 'ano',
    'Unused time on': 'Tempo não utilizado em',
    'Remaining time on': 'Tempo restante em',
    'after': 'após',
    'Subscription update': 'Atualização de assinatura',
    'Subscription creation': 'Criação de assinatura'
  },
  'es-ES': {
    'at': 'a',
    'month': 'mes',
    'year': 'año',
    'Unused time on': 'Tiempo no utilizado en',
    'Remaining time on': 'Tiempo restante en',
    'after': 'después de',
    'Subscription update': 'Actualización de suscripción',
    'Subscription creation': 'Creación de suscripción'
  },
  'en-US': {
    'at': 'at',
    'month': 'month',
    'year': 'year',
    'Unused time on': 'Unused time on',
    'Remaining time on': 'Remaining time on',
    'after': 'after',
    'Subscription update': 'Subscription update',
    'Subscription creation': 'Subscription creation'
  }
};

/**
 * Converte data no formato "DD Mon YYYY" para formato localizado
 * Exemplo: "17 Dec 2025" → "17/12/2025" (pt-BR) ou "17/12/2025" (es-ES)
 */
function translateDate(dateStr: string, language: string): string {
  // Padrão: DD Mon YYYY (ex: "17 Dec 2025")
  const datePattern = /(\d{1,2})\s+(\w{3})\s+(\d{4})/;
  const match = dateStr.match(datePattern);
  
  if (!match) return dateStr;
  
  const [, day, monthEn, year] = match;
  
  // Mapeamento de meses para números
  const monthToNumber: Record<string, string> = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
    'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
    'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };
  
  const monthNum = monthToNumber[monthEn];
  if (!monthNum) return dateStr;
  
  // Formatar como DD/MM/YYYY para pt-BR e es-ES, MM/DD/YYYY para en-US
  if (language === 'en-US') {
    return `${monthNum}/${day.padStart(2, '0')}/${year}`;
  }
  
  return `${day.padStart(2, '0')}/${monthNum}/${year}`;
}

/**
 * Traduz descrições do Stripe para o idioma selecionado
 * 
 * Padrões suportados:
 * - "1 × ProductName (at R$ X.XX / month)" → "1 × ProductName (por R$ X,XX / mês)"
 * - "Unused time on ProductName after DD Mon YYYY" → "Tempo não utilizado em ProductName após DD/MM/YYYY"
 * - "Remaining time on ProductName" → "Tempo restante em ProductName"
 * 
 * @param description - Descrição original do Stripe (em inglês)
 * @param language - Idioma de destino ('pt-BR' | 'en-US' | 'es-ES')
 * @returns Descrição traduzida
 */
export function translateStripeDescription(
  description: string | null | undefined,
  language: string = 'pt-BR'
): string {
  if (!description) return '';
  
  // Se não for um idioma suportado, retorna original
  const supportedLanguages = ['pt-BR', 'en-US', 'es-ES'];
  if (!supportedLanguages.includes(language)) {
    return description;
  }
  
  // Se já está em inglês e idioma é inglês, retorna original
  if (language === 'en-US') {
    return description;
  }
  
  let translated = description;
  const terms = termTranslations[language] || termTranslations['pt-BR'];
  
  // Padrão 1: "(at R$ X.XX / month)" ou "(at R$ X.XX / year)"
  // Regex: captura o valor e a unidade de tempo
  const pricePattern = /\(at\s+([A-Z$R]+\s*[\d.,]+)\s*\/\s*(month|year)\)/gi;
  translated = translated.replace(pricePattern, (match, price, period) => {
    const translatedPeriod = terms[period.toLowerCase()] || period;
    // Formatar preço com vírgula para pt-BR/es-ES
    const formattedPrice = price.replace('.', ',');
    return `(${terms['at']} ${formattedPrice} / ${translatedPeriod})`;
  });
  
  // Padrão 2: "Unused time on X after DD Mon YYYY"
  const unusedTimePattern = /Unused time on\s+(.+?)\s+after\s+(\d{1,2}\s+\w{3}\s+\d{4})/gi;
  translated = translated.replace(unusedTimePattern, (match, productName, date) => {
    const translatedDate = translateDate(date, language);
    return `${terms['Unused time on']} ${productName} ${terms['after']} ${translatedDate}`;
  });
  
  // Padrão 3: "Remaining time on X after DD Mon YYYY"
  const remainingTimePattern = /Remaining time on\s+(.+?)\s+after\s+(\d{1,2}\s+\w{3}\s+\d{4})/gi;
  translated = translated.replace(remainingTimePattern, (match, productName, date) => {
    const translatedDate = translateDate(date, language);
    return `${terms['Remaining time on']} ${productName} ${terms['after']} ${translatedDate}`;
  });
  
  // Padrão 4: "Remaining time on X" (sem data)
  const remainingOnlyPattern = /Remaining time on\s+(.+?)$/gi;
  translated = translated.replace(remainingOnlyPattern, (match, productName) => {
    return `${terms['Remaining time on']} ${productName}`;
  });
  
  // Padrão 5: "Unused time on X" (sem data específica, pode ter outras palavras depois)
  const unusedOnlyPattern = /Unused time on\s+(.+?)$/gi;
  if (!translated.includes(terms['Unused time on'])) {
    translated = translated.replace(unusedOnlyPattern, (match, rest) => {
      return `${terms['Unused time on']} ${rest}`;
    });
  }
  
  return translated;
}

/**
 * Traduz o status da fatura para o idioma selecionado
 * 
 * @param status - Status original ('paid' | 'pending' | 'failed')
 * @param language - Idioma de destino
 * @returns Status traduzido
 */
export function translateInvoiceStatus(
  status: string,
  language: string = 'pt-BR'
): string {
  const statusTranslations: Record<string, Record<string, string>> = {
    'pt-BR': {
      'paid': 'Pago',
      'pending': 'Pendente',
      'failed': 'Falhou',
      'open': 'Aberto',
      'draft': 'Rascunho',
      'void': 'Anulado',
      'uncollectible': 'Incobrável'
    },
    'es-ES': {
      'paid': 'Pagado',
      'pending': 'Pendiente',
      'failed': 'Fallido',
      'open': 'Abierto',
      'draft': 'Borrador',
      'void': 'Anulado',
      'uncollectible': 'Incobrable'
    },
    'en-US': {
      'paid': 'Paid',
      'pending': 'Pending',
      'failed': 'Failed',
      'open': 'Open',
      'draft': 'Draft',
      'void': 'Void',
      'uncollectible': 'Uncollectible'
    }
  };
  
  const translations = statusTranslations[language] || statusTranslations['pt-BR'];
  return translations[status.toLowerCase()] || status;
}
